import {
  LessonPlayerError,
  LessonPlayerErrors
} from '../../dashboard/components/LessonPlayerNative';
import React, { useEffect, useState } from 'react';
import { isEmpty, isFunction } from 'lodash';

import AlertDialog from './AlertDialog';
import AuthService from '../../shared/services/AuthService';
import LessonComponentService from '../../content/services/LessonComponentService';
import LessonPlayer from '../../content/components/LessonPlayer/LessonPlayer';
import { LessonPlayerProps } from '../../content/components/LessonPlayer/LessonPlayer';
import LessonPortfolioService from '../../assessments/services/LessonPortfolioService';
import { ThemeProvider } from '@mui/material/styles';
import useLessonColorTheme from '../../shared/hooks/useLessonColorTheme';
import { useLessonComponentsWithAnswers } from '../hooks/useLessonComponentsWithAnswers';
import { useLessonPlayerContext } from '../../dashboard/models/LessonPlayerContext';
import { usePortfolio } from '../../dashboard/hooks/usePortfolio';

interface LessonPlayerWithAnswersProps
  extends Omit<LessonPlayerProps, 'loading' | 'components'> {
  lessonkey: number;
  lessonVersion: number;
  layer: number;
  quizLocked: boolean;
  onCanFinalizeChange?: (value: boolean, userTotal: number, lessonTotal: number) => void; //* for marker view only
}

export function LessonPlayerWithAnswers(props: LessonPlayerWithAnswersProps) {
  const [lessonPlayerError, setLessonPlayerError] =
    useState<LessonPlayerError>(null);
  const { lessonkey, lessonVersion, layer } = props;
  const lessonPlayerContext = useLessonPlayerContext();
  const { lessonColorTheme } = useLessonColorTheme(
    props.displaySettings?.palette
  );

  const {
    loading,
    loadError,
    lessonComponents,
    upsertAnswer,
    savingAnswer,
    saveAnswerError,
    updateMark,
    deleteAnswer,
    deletingAnswer,
    deleteAnswerError,
    deletedAnswer
  } = useLessonComponentsWithAnswers(
    lessonkey,
    lessonVersion,
    lessonPlayerContext.lessonState?.answerParams?.userkey,
    layer,
    props.displayMode === 'learn' || props.displayMode === 'mark' //* with answers
  );

  const userkey = AuthService.Userkey;
  const { portfolio } = usePortfolio(userkey, lessonkey, layer);

  function handleSubmitAnswer(
    componentkey: number,
    answer: string,
    points: number
  ) {
    //* 'Completed' quiz lessons aren't allowed to save answers.
    //* 'Marker' view doesn't save answers.
    //* This handler is called from a Video component that has been watched
    //* to the end when it's first loaded, and also, when the lesson is about to close.
    //* Learners can save answer only when assessment started or when more work required states
    if (
      LessonPortfolioService.omitSubmitAnswer(
        props.lessonType,
        props.displayMode,
        props.quizLocked,
        props.state
      )
    ) {
      return;
    }
    if (answer) {
      upsertAnswer(componentkey, answer, points);
    } else {
      deleteAnswer(componentkey);
    }
  }

  useEffect(() => {
    if (loadError) {
      setLessonPlayerError(LessonPlayerErrors.load_lesson);
      return;
    }
    if (saveAnswerError) {
      setLessonPlayerError(LessonPlayerErrors.save_answer);
      return;
    }
    if (deleteAnswerError) {
      setLessonPlayerError(LessonPlayerErrors.delete_answer);
    }
  }, [loadError, saveAnswerError, deleteAnswerError]);

  //* for marker view only
  useEffect(() => {
    if (isFunction(props.onCanFinalizeChange) && lessonComponents.length > 0) {
      const canFianlize = LessonPortfolioService.canFinalize(lessonComponents);
      const userTotal = LessonComponentService.getUserTotalPoints(lessonComponents);
      const lessonTotal = LessonComponentService.getLessonTotalPoints('assessment', lessonComponents);
      props.onCanFinalizeChange(canFianlize, userTotal, lessonTotal);
    }
  }, [lessonComponents]);

  function handleUpdateMark(componentkey: number, mark: number) {
    if (isFunction(props.onUpdateMark)) {
      props.onUpdateMark(componentkey, mark);
    }
    updateMark(componentkey, mark);
  }

  return (
    <ThemeProvider theme={lessonColorTheme}>
      <LessonPlayer
        {...props}
        key={props.lessonkey}
        loading={isEmpty(lessonComponents) && loading}
        components={lessonComponents}
        inProgress={props.inProgress || savingAnswer || deletingAnswer}
        onSubmitAnswer={handleSubmitAnswer}
        onUpdateMark={handleUpdateMark}
        portfolioModified={portfolio?.modified}
        portfolioAssets={portfolio?.assets}
      />

      {lessonPlayerError && (
        <AlertDialog
          title={lessonPlayerError.title}
          message={lessonPlayerError.message}
          onClose={() => setLessonPlayerError(null)}
        />
      )}
    </ThemeProvider>
  );
}
